<template>
  <div class="toolbox-management">
    <el-container>
      <!-- 左侧：工具箱列表 -->
      <el-aside width="300px" class="toolbox-aside">
        <div class="aside-header">
          <h3>工具箱列表</h3>
          <div class="header-buttons">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddToolbox">新增</el-button>
            <el-button type="warning" size="small" icon="el-icon-edit" @click="handleEditToolbox" :disabled="!selectedToolbox">修改</el-button>
            <el-button type="danger" size="small" icon="el-icon-delete" @click="handleDeleteToolbox" :disabled="!selectedToolbox">删除</el-button>
          </div>
        </div>
        
        <el-scrollbar class="toolbox-list-scroll">
          <div class="toolbox-list" v-loading="toolboxListLoading">
            <div
              v-for="toolbox in toolboxList"
              :key="toolbox.toolboxNum"
              :class="['toolbox-item', { active: selectedToolbox && selectedToolbox.toolboxNum === toolbox.toolboxNum }]"
              @click="selectToolbox(toolbox)"
            >
              <div class="toolbox-info">
                <div class="toolbox-name">{{ toolbox.toolboxName }}</div>
                <div class="toolbox-detail">
                  <span>所属人员：{{ toolbox.custodian }}</span>
                </div>
                <div class="toolbox-detail">
                  <span>所属梯型：{{ toolbox.usageScenario || '-' }}</span>
                </div>
                <div class="toolbox-count">
                  <el-tag size="mini" type="info">仪器数：{{ toolbox.instrumentCount || 0 }}</el-tag>
                </div>
              </div>
            </div>
            
            <el-empty v-if="!toolboxListLoading && toolboxList.length === 0" description="暂无工具箱" :image-size="100"></el-empty>
          </div>
        </el-scrollbar>
      </el-aside>

      <!-- 右侧：仪器列表 -->
      <el-main class="instrument-main">
        <div v-if="selectedToolbox">
          <div class="main-header">
            <div class="header-content">
              <h3>工具箱：{{ selectedToolbox.toolboxName }} (所属人员：{{ selectedToolbox.custodian }})</h3>
              <div class="header-stats">
                <el-tag type="info" size="small">所属梯型：{{ selectedToolbox.usageScenario || '-' }}</el-tag>
                <el-tag type="success" size="small">仪器总数：{{ instrumentList.length }}</el-tag>
                <el-tag type="warning" size="small" v-if="selectedInstruments.length > 0">已选：{{ selectedInstruments.length }}</el-tag>
              </div>
            </div>
          </div>

          <!-- 搜索和操作栏 -->
          <el-form :model="queryParams" size="small" :inline="true" class="search-form">
            <el-form-item label="仪器编号">
              <el-input v-model="queryParams.eqptNum" placeholder="请输入仪器编号" clearable style="width: 200px" @keyup.enter.native="getInstrumentList" />
            </el-form-item>
            <el-form-item label="仪器名称">
              <el-input v-model="queryParams.eqptName" placeholder="请输入仪器名称" clearable style="width: 200px" @keyup.enter.native="getInstrumentList" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-search" @click="getInstrumentList">搜索</el-button>
              <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
              <el-button type="success" icon="el-icon-plus" @click="handleAddInstrument">新增仪器</el-button>
              <el-button type="danger" icon="el-icon-delete" @click="handleRemoveInstruments" :disabled="selectedInstruments.length === 0">删除仪器</el-button>
            </el-form-item>
          </el-form>

          <!-- 仪器列表表格 -->
          <el-table
            :data="instrumentList"
            border
            v-loading="instrumentListLoading"
            @selection-change="handleSelectionChange"
            style="width: 100%"
          >
            <el-table-column type="selection" width="55" align="center" fixed="left"></el-table-column>
            <el-table-column label="序号" type="index" width="60" align="center" fixed="left"></el-table-column>
            <el-table-column label="仪器编号" prop="eqptNum" min-width="180" show-overflow-tooltip fixed="left"></el-table-column>
            <el-table-column label="仪器名称" prop="eqptName" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column label="型号规格" prop="eqptSpecification" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column label="生产厂家" prop="eqptManufacturer" min-width="150" show-overflow-tooltip></el-table-column>
            <el-table-column label="出厂编号" prop="eqptFactoryNumber" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column label="测量范围" prop="eqptMeasuringRange" min-width="220" show-overflow-tooltip></el-table-column>
            <el-table-column label="精度等级" prop="eqptPrecision" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column label="购置日期" prop="eqptPurchaseDate" min-width="120" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ formatDate(scope.row.eqptPurchaseDate) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="单价(元)" prop="unitPrice" min-width="100" show-overflow-tooltip></el-table-column>
            <el-table-column label="量值溯源方式" prop="sourceType" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column label="检定/校准周期" prop="eqptVerificationCycle" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column label="检定/校准单位" prop="eqptVerificationUnit" min-width="220" show-overflow-tooltip></el-table-column>
            <el-table-column label="检定/校准证书编号" prop="certificateNo" min-width="160" show-overflow-tooltip></el-table-column>
            <el-table-column label="检定/校准日期" prop="eqptVerificationDate" min-width="120" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ formatDate(scope.row.eqptVerificationDate) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="有效期至" prop="eqptVerificationValidity" min-width="120" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ formatDate(scope.row.eqptVerificationValidity) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="现状" prop="eqptSituation" min-width="100" show-overflow-tooltip></el-table-column>
            <el-table-column label="使用科室" prop="eqptDepartment" min-width="100" show-overflow-tooltip></el-table-column>
            <el-table-column label="保管人" prop="eqptCustodian" min-width="100" show-overflow-tooltip></el-table-column>
          </el-table>
          
          <!-- 空状态 -->
          <el-empty v-if="!instrumentListLoading && instrumentList.length === 0" description="该工具箱暂无仪器" :image-size="150"></el-empty>
        </div>

        <el-empty v-else description="请选择工具箱" :image-size="200"></el-empty>
      </el-main>
    </el-container>

    <!-- 工具箱配置对话框（新增/编辑） -->
    <el-dialog
      :title="toolboxDialogTitle"
      :visible.sync="toolboxDialogVisible"
      width="500px"
      @close="resetToolboxForm"
    >
      <el-form :model="toolboxForm" :rules="toolboxRules" ref="toolboxFormRef" label-width="100px">
        <el-form-item label="名称" prop="toolboxName">
          <el-input v-model="toolboxForm.toolboxName" placeholder="请输入工具箱名称，例如：10号工具箱"></el-input>
        </el-form-item>
        <el-form-item label="使用场合" prop="usageScenario">
          <el-select v-model="toolboxForm.usageScenario" placeholder="请选择使用场合" style="width: 100%">
            <el-option label="直梯" value="直梯"></el-option>
            <el-option label="扶梯" value="扶梯"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="责任人员" prop="custodian">
          <el-select 
            v-model="toolboxForm.custodian" 
            placeholder="请选择责任人员" 
            filterable 
            style="width: 100%"
            @change="handleCustodianChange"
          >
            <el-option
              v-for="emp in employeeList"
              :key="emp.employeePid"
              :label="emp.employeeName"
              :value="emp.employeeName"
              :data-pid="emp.employeePid"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="toolboxForm.remark" type="textarea" :rows="3" placeholder="请输入备注"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="toolboxDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitToolboxForm" :loading="loading">确定</el-button>
      </span>
    </el-dialog>

    <!-- 选择仪器对话框 -->
    <el-dialog
      title="选择仪器"
      :visible.sync="instrumentSelectDialogVisible"
      width="80%"
    >
      <el-form :inline="true" size="small" class="available-search-form">
        <el-form-item label="仪器编号">
          <el-input v-model="availableQueryParams.eqptNum" placeholder="请输入" clearable style="width: 150px" @keyup.enter.native="getAvailableInstruments" />
        </el-form-item>
        <el-form-item label="仪器名称">
          <el-input v-model="availableQueryParams.eqptName" placeholder="请输入" clearable style="width: 150px" @keyup.enter.native="getAvailableInstruments" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="getAvailableInstruments">搜索</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetAvailableSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table
        :data="availableInstrumentList"
        @selection-change="handleAvailableSelectionChange"
        v-loading="loading"
        border
        max-height="400"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column label="仪器编号" prop="eqptNum" min-width="120"></el-table-column>
        <el-table-column label="仪器名称" prop="eqptName" min-width="120"></el-table-column>
        <el-table-column label="型号规格" prop="eqptSpecification" min-width="100"></el-table-column>
        <el-table-column label="保管人" prop="eqptCustodian" min-width="80"></el-table-column>
        <el-table-column label="现状" prop="eqptSituation" min-width="80"></el-table-column>
      </el-table>
      
      <el-empty v-if="!loading && availableInstrumentList.length === 0" description="暂无可用仪器" :image-size="100"></el-empty>

      <span slot="footer">
        <el-button @click="instrumentSelectDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAddInstruments" :loading="loading">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getToolboxList,
  addToolbox,
  updateToolbox,
  deleteToolbox,
  getToolboxInstruments,
  getAvailableInstruments,
  addInstrumentsToToolbox,
  removeInstrumentsFromToolbox,
  queryEmployee
} from '@/api/contract/contract'

export default {
  name: 'ToolboxManagement',
  data() {
    return {
      // 加载状态
      loading: false,
      toolboxListLoading: false,
      instrumentListLoading: false,
      
      // 工具箱列表
      toolboxList: [],
      selectedToolbox: null,

      // 人员列表
      employeeList: [],

      // 仪器列表
      instrumentList: [],
      selectedInstruments: [],
      queryParams: {
        eqptNum: '',
        eqptName: ''
      },
      
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },

      // 工具箱对话框
      toolboxDialogVisible: false,
      toolboxDialogTitle: '新增工具箱',
      toolboxDialogMode: 'add', // 'add' 或 'edit'
      toolboxForm: {
        toolboxId: null,
        toolboxNum: '', // 添加 toolboxNum 字段
        toolboxName: '',
        usageScenario: '',
        custodian: '',
        custodianPid: '',
        remark: ''
      },
      toolboxRules: {
        toolboxName: [{ required: true, message: '请输入工具箱名称', trigger: 'blur' }],
        usageScenario: [{ required: true, message: '请选择使用场合', trigger: 'change' }],
        custodian: [{ required: true, message: '请选择责任人员', trigger: 'change' }]
      },

      // 选择仪器对话框
      instrumentSelectDialogVisible: false,
      availableInstrumentList: [],
      selectedAvailableInstruments: [],
      availableQueryParams: {
        eqptNum: '',
        eqptName: ''
      }
    }
  },
  created() {
    this.getToolboxList()
    this.getEmployeeList()
  },
  methods: {
    // 获取人员列表
    async getEmployeeList() {
      try {
        // 请求所有人员数据（设置较大的pageSize）
        const res = await queryEmployee({
          currPage: 1,
          pageSize: 1000  // 获取所有人员
        })
        console.log('人员列表接口返回:', res)
        
        // 处理不同的返回格式
        if (res.dataList && Array.isArray(res.dataList)) {
          // 直接返回dataList的情况
          this.employeeList = res.dataList
          console.log(`人员列表数据(dataList): 共${this.employeeList.length}条`)
        } else if (res.retCode === 'ok' && res.additionalData) {
          // 标准格式
          this.employeeList = res.additionalData
          console.log(`人员列表数据(additionalData): 共${this.employeeList.length}条`)
        } else if (res.code === 200 && res.data) {
          // 另一种格式
          this.employeeList = res.data
          console.log(`人员列表数据(data): 共${this.employeeList.length}条`)
        } else {
          console.warn('未识别的返回格式:', res)
          this.employeeList = []
        }
      } catch (error) {
        console.error('获取人员列表异常:', error)
        this.employeeList = []
      }
    },

    // 责任人选择变化时，同时保存employeePid
    handleCustodianChange(employeeName) {
      const employee = this.employeeList.find(emp => emp.employeeName === employeeName)
      if (employee) {
        this.toolboxForm.custodianPid = employee.employeePid
      }
    },

    // 获取工具箱列表
    async getToolboxList() {
      this.toolboxListLoading = true
      try {
        const res = await getToolboxList()
        if (res.retCode === 'ok') {
          this.toolboxList = res.additionalData || []
          // 如果当前选中的工具箱被删除了，清空选中状态
          if (this.selectedToolbox) {
            const exists = this.toolboxList.find(t => t.toolboxNum === this.selectedToolbox.toolboxNum)
            if (!exists) {
              this.selectedToolbox = null
              this.instrumentList = []
            }
          }
        } else {
          this.$message.error(res.retMsg || '获取工具箱列表失败')
        }
      } catch (error) {
        console.error('获取工具箱列表失败:', error)
        this.$message.error('获取工具箱列表失败，请稍后重试')
      } finally {
        this.toolboxListLoading = false
      }
    },

    // 选择工具箱
    selectToolbox(toolbox) {
      this.selectedToolbox = toolbox
      this.queryParams = { eqptNum: '', eqptName: '' }
      this.selectedInstruments = []
      this.getInstrumentList()
    },

    // 获取仪器列表
    async getInstrumentList() {
      if (!this.selectedToolbox) return

      this.instrumentListLoading = true
      try {
        const res = await getToolboxInstruments(this.selectedToolbox.toolboxNum)
        if (res.retCode === 'ok') {
          let list = res.additionalData || []
          
          // 前端搜索过滤
          if (this.queryParams.eqptNum) {
            list = list.filter(item => 
              item.eqptNum && item.eqptNum.toLowerCase().includes(this.queryParams.eqptNum.toLowerCase())
            )
          }
          if (this.queryParams.eqptName) {
            list = list.filter(item => 
              item.eqptName && item.eqptName.toLowerCase().includes(this.queryParams.eqptName.toLowerCase())
            )
          }
          
          this.instrumentList = list
          this.pagination.total = list.length
        } else {
          this.$message.error(res.retMsg || '获取仪器列表失败')
        }
      } catch (error) {
        console.error('获取仪器列表失败:', error)
        this.$message.error('获取仪器列表失败，请稍后重试')
      } finally {
        this.instrumentListLoading = false
      }
    },

    // 新增工具箱
    handleAddToolbox() {
      this.toolboxDialogMode = 'add'
      this.toolboxDialogTitle = '新增工具箱'
      this.toolboxDialogVisible = true
    },

    // 修改工具箱
    handleEditToolbox() {
      if (!this.selectedToolbox) {
        this.$message.warning('请先选择工具箱')
        return
      }
      
      this.toolboxDialogMode = 'edit'
      this.toolboxDialogTitle = '修改工具箱'
      
      // 填充表单数据
      this.toolboxForm = {
        toolboxId: this.selectedToolbox.toolboxId,
        toolboxNum: this.selectedToolbox.toolboxNum, // 添加 toolboxNum 用于后端验证
        toolboxName: this.selectedToolbox.toolboxName,
        usageScenario: this.selectedToolbox.usageScenario,
        custodian: this.selectedToolbox.custodian,
        custodianPid: this.selectedToolbox.custodianPid,
        remark: this.selectedToolbox.remark || ''
      }
      
      this.toolboxDialogVisible = true
    },

    // 删除工具箱
    handleDeleteToolbox() {
      if (!this.selectedToolbox) {
        this.$message.warning('请先选择工具箱')
        return
      }

      this.$confirm(`确认要删除工具箱【${this.selectedToolbox.toolboxName}】吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteToolbox(this.selectedToolbox.toolboxNum)
          if (res.retCode === 'ok') {
            this.$message.success('删除成功')
            this.selectedToolbox = null
            this.instrumentList = []
            this.getToolboxList()
          } else {
            this.$message.error(res.retMsg || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败')
        }
      })
    },

    // 提交工具箱表单
    submitToolboxForm() {
      this.$refs.toolboxFormRef.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            let res
            if (this.toolboxDialogMode === 'edit') {
              // 修改模式
              res = await updateToolbox(this.toolboxForm)
            } else {
              // 新增模式
              res = await addToolbox(this.toolboxForm)
            }
            
            if (res.retCode === 'ok') {
              this.$message.success(this.toolboxDialogMode === 'edit' ? '修改成功' : '新增成功')
              this.toolboxDialogVisible = false
              await this.getToolboxList()
            } else {
              this.$message.error(res.retMsg || (this.toolboxDialogMode === 'edit' ? '修改失败' : '新增失败'))
            }
          } catch (error) {
            console.error('保存工具箱失败:', error)
            this.$message.error(this.toolboxDialogMode === 'edit' ? '修改失败，请稍后重试' : '新增失败，请稍后重试')
          } finally {
            this.loading = false
          }
        }
      })
    },

    // 重置工具箱表单
    resetToolboxForm() {
      this.$refs.toolboxFormRef && this.$refs.toolboxFormRef.resetFields()
      this.toolboxForm = {
        toolboxId: null,
        toolboxNum: '',
        toolboxName: '',
        usageScenario: '',
        custodian: '',
        custodianPid: '',
        remark: ''
      }
      this.toolboxDialogMode = 'add'
      this.toolboxDialogTitle = '新增工具箱'
    },

    // 新增仪器
    handleAddInstrument() {
      if (!this.selectedToolbox) {
        this.$message.warning('请先选择工具箱')
        return
      }
      // 重置搜索条件
      this.availableQueryParams = { eqptNum: '', eqptName: '' }
      this.instrumentSelectDialogVisible = true
      this.getAvailableInstruments()
    },

    // 获取可用仪器列表
    async getAvailableInstruments() {
      if (!this.selectedToolbox) {
        this.$message.warning('请先选择工具箱')
        return
      }

      this.loading = true
      try {
        const params = {
          toolboxNum: this.selectedToolbox.toolboxNum, // 传递当前工具箱号，用于排除已在该工具箱中的仪器
          eqptNum: this.availableQueryParams.eqptNum,
          eqptName: this.availableQueryParams.eqptName
        }
        console.log('查询可用仪器参数:', params)
        
        const res = await getAvailableInstruments(params)
        if (res.retCode === 'ok') {
          this.availableInstrumentList = res.additionalData || []
          if (this.availableInstrumentList.length === 0) {
            this.$message.info('暂无可用仪器')
          }
        } else {
          this.$message.error(res.retMsg || '获取可用仪器列表失败')
        }
      } catch (error) {
        console.error('获取可用仪器列表失败:', error)
        this.$message.error('获取可用仪器列表失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 提交添加仪器
    async submitAddInstruments() {
      if (this.selectedAvailableInstruments.length === 0) {
        this.$message.warning('请选择要添加的仪器')
        return
      }

      this.loading = true
      try {
        const data = {
          toolboxNum: this.selectedToolbox.toolboxNum,
          eqptNumList: this.selectedAvailableInstruments.map(item => item.eqptNum)
        }
        const res = await addInstrumentsToToolbox(data)
        if (res.retCode === 'ok') {
          this.$message.success(`成功添加${this.selectedAvailableInstruments.length}台仪器`)
          this.instrumentSelectDialogVisible = false
          this.selectedAvailableInstruments = []
          await this.getInstrumentList()
          await this.getToolboxList() // 刷新工具箱列表（更新仪器数量）
        } else {
          this.$message.error(res.retMsg || '添加失败')
        }
      } catch (error) {
        console.error('添加仪器失败:', error)
        this.$message.error('添加失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 删除仪器
    handleRemoveInstruments() {
      if (this.selectedInstruments.length === 0) {
        this.$message.warning('请选择要删除的仪器')
        return
      }

      this.$confirm(`确认要删除选中的${this.selectedInstruments.length}台仪器吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.loading = true
        try {
          const data = {
            toolboxNum: this.selectedToolbox.toolboxNum,
            eqptNumList: this.selectedInstruments.map(item => item.eqptNum)
          }
          const res = await removeInstrumentsFromToolbox(data)
          if (res.retCode === 'ok') {
            this.$message.success(`成功删除${this.selectedInstruments.length}台仪器`)
            this.selectedInstruments = []
            await this.getInstrumentList()
            await this.getToolboxList() // 刷新工具箱列表（更新仪器数量）
          } else {
            this.$message.error(res.retMsg || '删除失败')
          }
        } catch (error) {
          console.error('删除仪器失败:', error)
          this.$message.error('删除失败，请稍后重试')
        } finally {
          this.loading = false
        }
      }).catch(() => {
        // 用户取消操作
      })
    },

    // 仪器选择变化
    handleSelectionChange(selection) {
      this.selectedInstruments = selection
    },

    // 可用仪器选择变化
    handleAvailableSelectionChange(selection) {
      this.selectedAvailableInstruments = selection
    },
    
    // 重置搜索
    resetSearch() {
      this.queryParams = { eqptNum: '', eqptName: '' }
      this.getInstrumentList()
    },
    
    // 重置可用仪器搜索
    resetAvailableSearch() {
      this.availableQueryParams = { eqptNum: '', eqptName: '' }
      this.getAvailableInstruments()
    },
    
    // 刷新数据
    refreshData() {
      this.getToolboxList()
      if (this.selectedToolbox) {
        this.getInstrumentList()
      }
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      // 如果是字符串，直接返回前10位（yyyy-MM-dd）
      if (typeof date === 'string') {
        return date.substring(0, 10)
      }
      // 如果是Date对象，格式化
      if (date instanceof Date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      return ''
    }
  }
}
</script>

<style scoped lang="scss">
.toolbox-management {
  height: calc(100vh - 120px);
  padding: 20px;
  background: #f0f2f5;

  .el-container {
    height: 100%;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .toolbox-aside {
    border-right: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;

    .aside-header {
      padding: 16px;
      border-bottom: 1px solid #e8e8e8;
      background: #fafafa;

      h3 {
        margin: 0 0 12px 0;
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }

      .header-buttons {
        display: flex;
        gap: 8px;
        
        .el-button {
          flex: 1;
        }
      }
    }

    .toolbox-list-scroll {
      flex: 1;
      overflow: hidden;
    }

    .toolbox-list {
      padding: 8px;
      min-height: 200px;

      .toolbox-item {
        padding: 12px;
        margin-bottom: 8px;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s;
        background: #fff;

        &:hover {
          border-color: #409eff;
          background: #ecf5ff;
          transform: translateX(2px);
        }

        &.active {
          border-color: #409eff;
          background: #ecf5ff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
        }

        .toolbox-name {
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 8px;
          color: #303133;
        }

        .toolbox-detail {
          font-size: 12px;
          color: #606266;
          margin-bottom: 4px;
          
          span {
            display: inline-block;
          }
        }

        .toolbox-count {
          margin-top: 8px;
        }
      }
    }
  }

  .instrument-main {
    padding: 20px;
    
    .main-header {
      padding-bottom: 16px;
      border-bottom: 2px solid #e8e8e8;
      margin-bottom: 16px;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }

      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
      
      .header-stats {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
    }

    .search-form {
      margin-bottom: 16px;
      padding: 16px;
      background: #fafafa;
      border-radius: 4px;
      
      .el-form-item {
        margin-bottom: 0;
      }
    }
    
    .el-table {
      margin-top: 16px;
    }
  }
}

/* 可用仪器搜索表单样式 */
.available-search-form {
  margin-bottom: 16px;
  
  .el-form-item {
    margin-bottom: 0;
  }
}

/* 对话框样式优化 */
::v-deep .el-dialog {
  border-radius: 8px;
  
  .el-dialog__header {
    background: #fafafa;
    border-bottom: 1px solid #e8e8e8;
    padding: 16px 20px;
    
    .el-dialog__title {
      font-size: 16px;
      font-weight: 500;
    }
  }
  
  .el-dialog__body {
    padding: 20px;
  }
  
  .el-dialog__footer {
    border-top: 1px solid #e8e8e8;
    padding: 12px 20px;
  }
}

/* 表格样式优化 */
::v-deep .el-table {
  th {
    background: #fafafa;
    color: #303133;
    font-weight: 500;
  }
  
  .el-table__empty-block {
    min-height: 200px;
  }
}

/* 空状态样式 */
::v-deep .el-empty {
  padding: 40px 0;
  
  .el-empty__description {
    margin-top: 16px;
    color: #909399;
  }
}

/* 响应式设计 */
@media screen and (max-width: 1200px) {
  .toolbox-management {
    .toolbox-aside {
      width: 250px !important;
    }
  }
}

@media screen and (max-width: 768px) {
  .toolbox-management {
    padding: 10px;
    
    .el-container {
      flex-direction: column;
    }
    
    .toolbox-aside {
      width: 100% !important;
      max-height: 300px;
      border-right: none;
      border-bottom: 1px solid #e8e8e8;
    }
  }
}
</style>
